import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router';
import { Web5Provider, TwoFA, Login } from '@Sage/ssm-web5';

import { Core } from 'managers';
import { ADMIN_API_URL, BACKEND2_API_URL, WEBAPP_URL } from 'constants/Environment';
import AppLoader from 'components/AppLoader';
import { AuthenticationActions } from 'actions';
import { DarkSageLogo } from 'components/SvgIcons';
import { intercomHideAndShow } from 'utils/intercom';
import theme from 'utils/theme';
import { parseAuthHash, sanitizeEntityToGo } from 'utils/authCallback';

import './styles.scss';
import { externalAuthentication } from 'actions/Authentication';

const RECOVER_PASSWORD_URL = `${WEBAPP_URL}/#/recover-password`;

const mapStateToProps = ({ authentication, literal }) => ({
    auth: authentication,
    locales: literal.literals,
});

const mapDispatchToProps = (dispatch) => ({
    login: bindActionCreators(AuthenticationActions, dispatch).login,
    close2FA: bindActionCreators(AuthenticationActions, dispatch).close2FA,
    finish2FA: bindActionCreators(AuthenticationActions, dispatch).finish2FA,
    oauthCallbackLogin: bindActionCreators(AuthenticationActions, dispatch).oauthCallbackLogin,
    loginToken: bindActionCreators(AuthenticationActions, dispatch).loginToken,
});

function AuthenticationPage({
    auth = {},
    locales,
    login = () => null,
    close2FA,
    finish2FA,
    oauthCallbackLogin,
    loginToken,
}) {
    const navigate = useNavigate();
    const entityToGoRef = useRef(sanitizeEntityToGo(localStorage.getItem('entityToGo')));
    const authHash = useMemo(() => parseAuthHash(window.location.hash), []);

    const { oauthRequest, authenticated, canSwitchImpl, is2FAOpen, data2FA, error } = auth;

    useEffect(() => {
        intercomHideAndShow('hide');

        const { webToken, sandboxToken, state, oauthUrl, entityToGo } = authHash;

        if (webToken) {
            if (entityToGo) {
                localStorage.setItem('entityToGo', entityToGo);
                entityToGoRef.current = entityToGo;
            }
            navigate('/auth/login');
            loginToken(webToken, true);
            return;
        }

        if (sandboxToken) {
            navigate('/auth/login');
            loginToken(sandboxToken);
            return;
        }

        if (state && oauthUrl) {
            navigate('/auth/login');
            oauthCallbackLogin(state, oauthUrl);
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!authenticated) {
            return;
        }

        if (canSwitchImpl && !authHash.sandboxToken) {
            navigate('/choose');
        } else if (entityToGoRef.current) {
            const destination = entityToGoRef.current;
            localStorage.removeItem('entityToGo');
            entityToGoRef.current = null;
            navigate(`/app/${destination}`);
        } else {
            navigate('/app/workspace');
        }
    }, [authenticated, canSwitchImpl, navigate, authHash]);

    const onClose = useCallback((err) => close2FA(err), [close2FA]);

    const onFinish = useCallback((data) => finish2FA(data), [finish2FA]);

    const handleLogin = useCallback((username, password) => login({ username, password }), [login]);

    const onAuthType = useCallback((result) => {
        externalAuthentication(result.authenticationServer.address);
    }, []);

    const web5Context = useMemo(
        () => ({
            token: Core.cacheManager.getTokenStore(),
            apiBaseURL: ADMIN_API_URL,
            b2URL: BACKEND2_API_URL,
            locales,
            platform: 107,
        }),
        [locales],
    );

    return (
        <div className="container auth-container">
            <Web5Provider context={web5Context} theme={theme}>
                {oauthRequest && !error && <AppLoader />}
                {!oauthRequest && (
                    <Fragment>
                        <Login
                            logo={<DarkSageLogo />}
                            onAuthType={onAuthType}
                            onLogin={handleLogin}
                            recoverPasswordURL={RECOVER_PASSWORD_URL}
                            usernameDataTest="authentication-username"
                            passwordDataTest="authentication-password"
                            submitDataTest="authentication-submit"
                        />
                    </Fragment>
                )}
                {data2FA?.ready && (
                    <TwoFA
                        isOpen={is2FAOpen}
                        onClose={onClose}
                        onFinish={onFinish}
                        isFirstTime={data2FA.isFirstTime}
                        isPhoneEditable={data2FA.isFirstTime || !data2FA.numOKAccess}
                        prefixId={data2FA.prefixId}
                        phoneNumber={data2FA.phoneNumber}
                        prefixList={data2FA.prefixList}
                        credentials={data2FA.credentials}
                    />
                )}
            </Web5Provider>
        </div>
    );
}

AuthenticationPage.propTypes = {
    auth: PropTypes.shape({
        authenticated: PropTypes.bool,
    }),
    login: PropTypes.func,
    close2FA: PropTypes.func,
    finish2FA: PropTypes.func,
};

export default memo(connect(mapStateToProps, mapDispatchToProps)(AuthenticationPage));
